import React, { useEffect, useState } from "react";
import axios from "axios";
import useProviderProfile from "../hooks/useProviderProfile";
import { toast } from "react-toastify";
import { SERVER_URL } from "../config";

interface DeniedPrescription {
  id: string;
  patient: {
    patientProfile: {
      firstName: string;
      lastName: string;
    };
  };
  medication: {
    name: string;
  };
  dosage: string;
  strength: string;
  quantity: number;
  instructions: string;
}

const DeniedPrescriptions: React.FC = () => {
  const { provider } = useProviderProfile();
  const clinicId = provider?.clinic?.id;

  const [prescriptions, setPrescriptions] = useState<DeniedPrescription[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const limit = 10;

  const fetchPrescriptions = async () => {
    if (!clinicId) {
      setError("Clinic not found");
      return;
    }
    //
    setLoading(true);
    try {
      const offset = page * limit;
      const response = await axios.get(
        `${SERVER_URL}/api/prescription/denied-prescriptions`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            clinicId,
            limit,
            offset,
          },
        }
      );
      setPrescriptions(response.data);
      setError("");
    } catch (err: any) {
      console.error("Error fetching denied prescriptions:", err);
      setError("Failed to fetch denied prescriptions.");
      toast.error("Failed to fetch denied prescriptions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clinicId) {
      fetchPrescriptions();
    }
  }, [clinicId, page]);

  const handleNext = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const handlePrevious = () => {
    if (page > 0) {
      setPage((prevPage) => prevPage - 1);
    }
  };

  if (loading) return <p>Loading denied prescriptions...</p>;
  if (error) return <p>{error}</p>;
  if (prescriptions.length === 0) return <p>No denied prescriptions.</p>;

  return (
    <div className="prescriptions-container">
      <h2>Denied Prescriptions</h2>
      <table className="prescription-table">
        <thead>
          <tr>
            <th>Patient</th>
            <th>Medication</th>
            <th>Dosage</th>
            <th>Strength</th>
            <th>Quantity</th>
            <th>More Info</th>
          </tr>
        </thead>
        <tbody>
          {prescriptions.map((p) => (
            <tr key={p.id}>
              <td>
                {p.patient.patientProfile.firstName}{" "}
                {p.patient.patientProfile.lastName}
              </td>
              <td>{p.medication.name}</td>
              <td>{p.dosage}</td>
              <td>{p.strength}</td>
              <td>{p.quantity}</td>
              <td>{p.instructions}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pagination-controls">
        <button onClick={handlePrevious} disabled={page === 0}>
          Previous
        </button>
        <span>Page {page + 1}</span>
        <button onClick={handleNext}>Next</button>
      </div>
    </div>
  );
};

export default DeniedPrescriptions;
